import { useCallback, useEffect, useState } from "react"

/**
 * Installation sur l'écran d'accueil.
 *
 * Deux mondes, et il faut couvrir les deux :
 *   — Chrome/Edge/Android émettent `beforeinstallprompt`, qu'on capture pour pouvoir
 *     déclencher la vraie boîte d'installation au bon moment (l'événement n'est
 *     rejouable qu'une fois, d'où la mise de côté) ;
 *   — Safari iOS n'émet rien du tout : l'installation passe obligatoirement par le menu
 *     Partager. On ne peut alors qu'expliquer le geste.
 *
 * `installed` sert à masquer complètement l'entrée : proposer d'installer une app déjà
 * installée est le meilleur moyen de faire douter l'utilisateur.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

/** Page affichée dans l'app installée (et non dans un onglet du navigateur). */
export function detectInstalled(): boolean {
  if (typeof window === "undefined") return false
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true
  return (window.navigator as Navigator & { standalone?: boolean }).standalone === true
}

export function useInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(detectInstalled)

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => { setInstalled(true); setDeferred(null) }

    window.addEventListener("beforeinstallprompt", onBeforeInstall)
    window.addEventListener("appinstalled", onInstalled)

    const mq = window.matchMedia?.("(display-mode: standalone)")
    const onDisplayChange = () => setInstalled(detectInstalled())
    mq?.addEventListener?.("change", onDisplayChange)

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall)
      window.removeEventListener("appinstalled", onInstalled)
      mq?.removeEventListener?.("change", onDisplayChange)
    }
  }, [])

  /** true si la boîte native a été ouverte ; false s'il faut expliquer le geste à la main. */
  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferred) return false
    await deferred.prompt()
    await deferred.userChoice
    // L'événement n'est utilisable qu'une fois : le navigateur en réémettra un nouveau
    // si l'utilisateur a refusé et revient plus tard.
    setDeferred(null)
    return true
  }, [deferred])

  return { installed, canPrompt: deferred !== null, promptInstall }
}
